import { ASSET_PREFIX, colors } from 'utils/constants';
import React, { useState } from 'react';
import RoundedButton from 'components/RoundedButton';
import { validPassword } from 'utils/RegistrationValidator';
import { upgradeGuestAccount } from 'api/auth';
import { useUIStore } from 'hooks/stores/useUIStore';
import { observer } from 'mobx-react-lite';

export const Prompt = {
  INTRO_GENERAL: 'INTRO_GENERAL', // A general suggestion (e.g. shown on confirmation page).
  INTRO_BLOCKING_PRIZE_REDEMPTION: 'INTRO_BLOCKING_PRIZE_REDEMPTION',
};

type ICreateAccountPopupProps = {
  prompt?: (typeof Prompt)[keyof typeof Prompt]; // Optional. Default: Prompt.INTRO_GENERAL.
  userId: string;
  emailAddress: string;
  onClose: () => void;
};

const CreateAccountPopup = observer(
  ({
    prompt = Prompt.INTRO_GENERAL,
    userId,
    emailAddress,
    onClose,
  }: ICreateAccountPopupProps) => {
    const { displayToast } = useUIStore();

    const [loading, setLoading] = useState(false);
    const [password, setPassword] = useState('');

    const handleCreateAccount = async (e: any) => {
      e?.preventDefault();

      if (!validPassword(password)) {
        alert(
          `Passwords must be 8+ characters and must contain a number or special character.`
        );
        return;
      }

      setLoading(true);
      const result = await upgradeGuestAccount(emailAddress, password, userId);
      setLoading(false);

      if (result.successful) {
        // Reload page to save password in Safari
        window.location.reload();
      } else {
        displayToast({
          title: `Oops, something went wrong: (${result.reason})`,
          color: '#FF6600',
          timeout: 9000,
        });
        onClose();
      }
    };

    let promptContent;

    switch (prompt) {
      case Prompt.INTRO_GENERAL:
        promptContent = (
          <>
            <h3>Create Account?</h3>

            <p style={{ maxWidth: 320, marginBottom: '1.5em' }}>
              Want to have easy access to your numbers, prizes, and subscription
              info?
            </p>
          </>
        );
        break;

      case Prompt.INTRO_BLOCKING_PRIZE_REDEMPTION:
        promptContent = (
          <>
            <h3>Oops!</h3>

            <p style={{ maxWidth: 320, marginBottom: '1.5em' }}>
              You'll need to create an account to access your prizes.
            </p>
          </>
        );
        break;

      default:
        throw new Error(`Don't know what to show for prompt: ${prompt}`);
    }

    return (
      <div className="container" id="dimmedBackground">
        <div id="message">
          <img
            src={`${ASSET_PREFIX}/images/create-account-popup/profile-icon.svg`}
            alt=""
            style={{
              height: 40,
            }}
          />

          {promptContent}

          <form
            onSubmit={handleCreateAccount}
            style={{ width: '100%', maxWidth: 320, marginBottom: '0.5em' }}
          >
            <input
              className="neonInput inputForm"
              disabled
              value={emailAddress}
              style={{
                cursor: 'not-allowed',
              }}
            />
            <input
              className="neonInput inputForm"
              placeholder="Create password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
              }}
            />
            <input
              type="submit"
              style={{ visibility: 'hidden', position: 'absolute' }}
            />
          </form>

          <RoundedButton
            loading={loading}
            onClick={handleCreateAccount}
            style={{ width: '100%', maxWidth: 320 }}
          >
            Create Account Now
          </RoundedButton>

          <a
            onClick={onClose}
            style={{
              textDecoration: 'underline',
              marginTop: '1.5em',
              cursor: 'pointer',
            }}
          >
            I'll do it later
          </a>
        </div>

        <style jsx>{`
          .container {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            max-width: 100%;
            height: 100%;
            background-color: rgba(55, 55, 55, 0.8);
            z-index: 999;
          }

          #message {
            position: relative;
            background: url(${ASSET_PREFIX}/images/landing/green-background-circles.svg);
            color: ${colors.primaryBrandGray};
            top: 50%;
            left: 50%;
            width: 90%;
            max-width: 500px;
            max-height: 95%;
            overflow-y: auto;
            -webkit-transform: translate(-50%, -50%);
            transform: translate(-50%, -50%);

            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 2.4em 1.5em 4em 1.5em;
            border-radius: 7px;
            box-shadow: 0 0 15px #333;
            z-index: 1000;
            text-align: center;
          }

          :global(#message .inputForm) {
            padding: 10px 15px;
            border-radius: 18px;
            border: none;
            width: 100%;
            margin: 0.5em 0;
            font-weight: 600;
          }
        `}</style>
      </div>
    );
  }
);

export default CreateAccountPopup;
